'use client';

import * as React from 'react';

/* Month grid shared by the demo booking (/demo) and the interview booking
   (/careers/[slug]/apply): Monday-first, with weekends, past days, today, fully booked
   days and anything beyond the horizon disabled.

   Calendar days are Brussels days, not the visitor's: the grid is anchored on the
   Brussels date key so a `Date` built from local Y/M/D maps back to the same key. */

export function dateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function dateFromKey(key: string) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function formatBookingDate(date: Date, locale: string, opts: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : `${locale}-BE`, opts).format(date);
}

/** Monday to Friday, the default open days. */
const isWeekday = (day: Date) => day.getDay() >= 1 && day.getDay() <= 5;

export function BookingCalendar({
  locale, selected, onSelect, ariaPrev, ariaNext, today, max, monthOffset, onMonthOffset, isFull,
  isOpenDay = isWeekday,
}: {
  locale: string; selected: Date | null; onSelect: (d: Date) => void; ariaPrev: string; ariaNext: string;
  today: Date; max: Date; monthOffset: number; onMonthOffset: (n: number) => void;
  isFull: (day: Date) => boolean;
  /** Which weekdays this calendar is bookable at all — interviews run Tue–Fri. */
  isOpenDay?: (day: Date) => boolean;
}) {
  const first = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const leadingBlanks = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  // 2024-01-01 is a Monday — a fixed week to render localized weekday initials
  const weekdays = [...Array(7)].map((_, i) =>
    formatBookingDate(new Date(2024, 0, i + 1), locale, { weekday: 'short' }).replace('.', '').slice(0, 2));
  const cells: (Date | null)[] = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(first.getFullYear(), first.getMonth(), d));
  return (
    <div>
      <div className="b-cal-head">
        <span className="m">{formatBookingDate(first, locale, { month: 'long', year: 'numeric' })}</span>
        <div className="b-cal-nav">
          <button aria-label={ariaPrev} disabled={monthOffset === 0} onClick={() => onMonthOffset(monthOffset - 1)}>←</button>
          <button aria-label={ariaNext} disabled={monthOffset >= 2} onClick={() => onMonthOffset(monthOffset + 1)}>→</button>
        </div>
      </div>
      <div className="b-cal-grid">
        {weekdays.map((w, i) => <span className="wd" key={'w' + i}>{w}</span>)}
        {cells.map((day, i) => {
          if (!day) return <span key={i}></span>;
          const disabled = !isOpenDay(day) || day <= today || day > max || isFull(day);
          const isSelected = !!selected && day.getTime() === selected.getTime();
          const isToday = day.getTime() === today.getTime();
          return (
            <button key={i} disabled={disabled}
              className={'b-cal-d' + (isSelected ? ' sel' : '') + (isToday ? ' today' : '')}
              onClick={() => onSelect(day)}>
              {/* weekday label — only shown in chip mode (slot column open), where
                  the grid reflows to fewer columns and loses weekday alignment */}
              <span className="dw">{weekdays[(day.getDay() + 6) % 7]}</span>
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
